import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { classNames } from '../lib/format';
import type { Banner } from '../types';

const emptyBanner: Partial<Banner> = {
  title: '',
  eyebrow: '',
  body: '',
  ctaLabel: 'Ver más',
  ctaHref: '/',
  imageUrl: '',
  status: 'draft',
  sortOrder: 0
};

export function BannersPanel({ advanced }: { advanced: boolean }) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [editing, setEditing] = useState<Partial<Banner>>(emptyBanner);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      setBanners(await api.admin.banners());
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error cargando banners' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function reset() {
    setEditing(emptyBanner);
    setMessage(null);
  }

  async function uploadImage(file?: File | null) {
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const result = await api.admin.uploadAsset(file);
      setEditing((prev) => ({ ...prev, imageUrl: result.url }));
      setMessage({ tone: 'ok', text: '✓ Imagen cargada.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo subir' });
    } finally {
      setUploading(false);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api.admin.saveBanner(editing);
      setEditing(saved);
      setMessage({ tone: 'ok', text: '✓ Banner guardado.' });
      await load();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string, title: string) {
    if (!window.confirm(`¿Archivar el banner "${title}"?`)) return;
    await api.admin.deleteBanner(id);
    if (editing.id === id) reset();
    await load();
  }

  return (
    <div className="admin-two-column">
      <section className="admin-card">
        <div className="admin-card-head">
          <h2>Banners</h2>
          <button className="ghost-button compact" onClick={reset}>+ Nuevo</button>
        </div>
        {loading ? (
          <p className="muted">Cargando...</p>
        ) : banners.length === 0 ? (
          <div className="empty-state subtle">
            <p>Aún no hay banners.</p>
            <p className="muted">Crea uno con el botón "Nuevo". Los banners en estado "Publicado" se muestran en la home en orden por "Orden".</p>
          </div>
        ) : (
          <ul className="banner-list">
            {banners.map((b) => (
              <li key={b.id} className={classNames('banner-list-row', editing.id === b.id && 'active')}>
                <button type="button" className="banner-row-main" onClick={() => { setEditing(b); setMessage(null); }}>
                  <div className="banner-row-thumb">
                    {b.imageUrl ? <img src={b.imageUrl} alt={b.title} /> : <span>{b.title.slice(0, 2).toUpperCase()}</span>}
                  </div>
                  <div className="banner-row-meta">
                    <strong>{b.title}</strong>
                    <small>{b.eyebrow || b.ctaLabel}</small>
                  </div>
                  <span className={classNames('promo-status', `status-${b.status}`)}>{b.status}</span>
                </button>
                {advanced && <button type="button" className="promo-row-delete" onClick={() => remove(b.id, b.title)}>×</button>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <form className="admin-card" onSubmit={save}>
        <div className="admin-card-head">
          <h2>{editing.id ? 'Editar banner' : 'Nuevo banner'}</h2>
          <button className="primary-button compact" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
        </div>

        <div className="form-grid">
          <label className="span-2">
            Título principal
            <input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Ej: Nueva temporada" required />
          </label>

          <label>
            Eyebrow / etiqueta
            <input value={editing.eyebrow || ''} onChange={(e) => setEditing({ ...editing, eyebrow: e.target.value })} placeholder="Ej: Otoño 2026" />
          </label>

          <label>
            Estado
            <select value={editing.status || 'draft'} onChange={(e) => setEditing({ ...editing, status: e.target.value as Banner['status'] })}>
              <option value="draft">Borrador</option>
              <option value="published">Publicado</option>
              <option value="archived">Archivado</option>
            </select>
          </label>

          <label className="span-2">
            Texto descriptivo
            <textarea rows={3} value={editing.body || ''} onChange={(e) => setEditing({ ...editing, body: e.target.value })} placeholder="Texto que aparece debajo del título" />
          </label>

          <label>
            Texto del botón
            <input value={editing.ctaLabel || ''} onChange={(e) => setEditing({ ...editing, ctaLabel: e.target.value })} placeholder="Ver colección" />
          </label>

          <label>
            Link del botón
            <input value={editing.ctaHref || ''} onChange={(e) => setEditing({ ...editing, ctaHref: e.target.value })} placeholder="/?cat=Fitness" />
          </label>

          {advanced && (
            <label>
              Orden (menor = primero)
              <input type="number" min={0} value={editing.sortOrder ?? 0} onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })} />
            </label>
          )}

          <div className="span-2">
            <label>
              Imagen del banner
              {editing.imageUrl && (
                <div className="banner-preview">
                  <img src={editing.imageUrl} alt="" />
                </div>
              )}
              <label className={classNames('upload-button', uploading && 'is-busy')} style={{ marginTop: 8 }}>
                {uploading ? 'Subiendo...' : (editing.imageUrl ? '↻ Cambiar imagen' : '+ Subir imagen')}
                <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} disabled={uploading} />
              </label>
            </label>
          </div>
        </div>

        {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
      </form>
    </div>
  );
}
